import { Button } from '@eventbrite/eds-button';
import { Icon } from '@eventbrite/eds-icon';
import { ClockChunky, MailChunky } from '@eventbrite/eds-iconography';
import { ProgressIndicator } from '@eventbrite/eds-progress-indicator';
import { gettext } from '@eventbrite/i18n';
import classNames from 'classnames';
import React from 'react';
import { sendVerificationEmailAction } from '../../../actions/emails';
import {
    VERIFICATION_EMAIL_STATUSES,
    VERIFICATION_STATUSES,
} from '../../../constants/status';
import {
    IConfigContextProps,
    useConfigContext,
} from '../../../contexts/ConfigContext';
import {
    IFrontendContext,
    useFrontendContext,
} from '../../../contexts/FrontendContext';
import {
    INotificationContext,
    useNotificationsContext,
} from '../../../contexts/NotificationsContext';
import { IUserContext, useUserContext } from '../../../contexts/UserContext';
import Header from '../../common/Header';
import './VerificationForm.scss';
import VerificationFormComponent, {
    IVerificationValuesProps,
} from './VerificationFormComponent';

export interface IVerificationFormProps {
    dataTestId: string;
    email?: string;
    onSubmitCode: (verificationCode: string) => void;
    target: string;
}

const SubtitleComponent = ({ email }: { email?: string }) => (
    <>
        <p>
            {gettext('We sent a verification code to %(email)s', {
                email,
            })}
        </p>
        <p>{gettext('Enter your code:')}</p>
    </>
);

const VerificationForm = ({
    dataTestId,
    email,
    onSubmitCode,
    target,
}: IVerificationFormProps) => {
    const frontendContext: IFrontendContext = useFrontendContext();
    const notificationsContext: INotificationContext =
        useNotificationsContext();
    const configContext: IConfigContextProps = useConfigContext();
    const userContext: IUserContext = useUserContext();

    const verificationEmailStatus =
        frontendContext.state.verificationEmailStatus;
    const verificationInvalidCode =
        userContext.state.emailVerificationStatus ===
        VERIFICATION_STATUSES.failed;
    const verificationMaxTries =
        userContext.state.emailVerificationStatus ===
        VERIFICATION_STATUSES.maxTries;
    const verificationUserError =
        verificationInvalidCode || verificationMaxTries;
    const verificationOngoing = frontendContext.state.isModalLoading;
    const isSendingEmail =
        verificationEmailStatus === VERIFICATION_EMAIL_STATUSES.ongoing;
    const emailLimitReached =
        verificationEmailStatus === VERIFICATION_EMAIL_STATUSES.limitReached;
    const formComponentClasses = classNames(
        'verification-form__component eds-align--center',
        {
            'eds-l-pad-bot-8': !verificationUserError,
            'verification-form__component--failed eds-l-mar-bot-6':
                verificationUserError,
            'verification-form__component--row': verificationOngoing,
        },
    );

    const _handleSubmitCode = (values: IVerificationValuesProps) => {
        let allValues = '';
        Object.keys(values).forEach((key: string) => {
            const value = (values as any)[key];

            if (value) {
                allValues += value;
            }
        });

        onSubmitCode(allValues);
    };

    let footerComponent = (
        <>
            <Icon type={<ClockChunky />} color="ui-700" />
            {gettext(
                'For your security, this code can only be used once. It expires in 10 minutes.',
            )}
        </>
    );

    if (verificationUserError) {
        const buttonText = verificationInvalidCode
            ? gettext('Resend code')
            : gettext('Send a new code');

        footerComponent = (
            <Button
                onClick={() => {
                    sendVerificationEmailAction({
                        email: email ?? '',
                        target,
                        contexts: {
                            userContext,
                            frontendContext,
                            notificationsContext,
                            configContext,
                        },
                    });
                }}
                type="submit"
                style="link"
                disabled={isSendingEmail}
                data-testid={`${dataTestId}-resend`}
                isLoading={isSendingEmail}
            >
                {buttonText}
            </Button>
        );
    }

    const loadingModalIndicator = verificationOngoing && (
        <ProgressIndicator
            size="medium-chunky"
            shape="circular"
            style="gradient"
        />
    );
    let headerProps = {
        titleText: gettext('Verify your account'),
        iconType: <MailChunky />,
        subtitleText: <SubtitleComponent email={email} />,
        iconColor: 'ui-700',
        iconBottomSpacing: 'eds-l-mar-bot-6',
        titleClassName: 'eds-text-hs eds-l-mar-bot-4',
        isLoading: isSendingEmail,
    };
    let modalBody = (
        <>
            <VerificationFormComponent
                dataTestId={`${dataTestId}-component`}
                onSubmitCode={_handleSubmitCode}
                invalidCode={verificationInvalidCode}
                maxTries={verificationMaxTries}
            />

            {loadingModalIndicator}
        </>
    );

    if (isSendingEmail) {
        headerProps = {
            ...headerProps,
            titleText: gettext('Verifying your account'),
            subtitleText: <p>{gettext('Please wait a second')}...</p>,
            isLoading: true,
        };
        modalBody = <>{loadingModalIndicator}</>;
        footerComponent = <></>;
    }

    if (emailLimitReached) {
        headerProps = {
            ...headerProps,
            iconType: <MailChunky />,
            titleText: gettext(
                'You made more than 10 requests to send a verification code to',
            ),
            subtitleText: <p>{email}</p>,
        };
        modalBody = <>{loadingModalIndicator}</>;
        footerComponent = (
            <>
                <Icon type={<ClockChunky />} color="ui-700" />
                {gettext(
                    'Please wait 1 hour before requesting a new verification code to try again.',
                )}
            </>
        );
    }

    return (
        <div
            className="verification-form-container eds-fx--fade-in eds-fx--delay-2"
            data-testid={dataTestId}
        >
            <Header {...headerProps} />

            <div className={formComponentClasses}>{modalBody}</div>
            <div className="verification-form__clarification eds-align--center eds-text-bs eds-text-color--ui-600">
                {footerComponent}
            </div>
        </div>
    );
};

export default VerificationForm;
